import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Plus, Trash2, Bell, BellOff, Search, Sparkles, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { isAuthenticated, getAuthHeader } from '../services/otpAuthService';

interface Tariffe {
  diritti?: number;
  bolli1?: number;
  bolli2?: number;
  costo?: number;
  diritti2?: number;
  diritti3?: number;
  diritti4?: number;
  gas?: number;
  contributi?: number;
  dirittiSted?: number;
  corrispettivoSted?: number;
  tributiSpeciali?: number;
  rimborsoMissioni?: number;
  esenzione?: boolean;
  cumulabile?: boolean;
}

interface Pratica {
  categoria: string;
  catalogo: string;
  descrizionePratica: string;
  tariffe?: Tariffe;
}

interface Interesse {
  _id: string;
  tariffario: string;
  codiceTariffa: string;
  attivo: boolean;
  notificheEmail: boolean;
  pratiche?: Pratica[];
  createdAt: string;
}

interface TariffaDisponibile {
  codiceTariffa: string;
  pratiche: Pratica[];
}

const RimborsoVoucherInteressiPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [interessi, setInteressi] = useState<Interesse[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [tariffarioSelezionato, setTariffarioSelezionato] = useState('NAZIONALE');
  const [searchQuery, setSearchQuery] = useState('');
  const [tariffeDisponibili, setTariffeDisponibili] = useState<TariffaDisponibile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingInteresse, setAddingInteresse] = useState(false);
  const [expandedInteressi, setExpandedInteressi] = useState<Set<string>>(new Set());
  const [expandedTariffe, setExpandedTariffe] = useState<Set<number>>(new Set());

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('voucher_interessi_tour');

  const tourSteps = [
    {
      target: '#tour-interessi-aggiungi',
      content: 'Clicca qui per aggiungere un nuovo interesse su una pratica specifica.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-interessi-info',
      content: 'Il sistema ti invierà una notifica ogni volta che viene pubblicato un voucher che corrisponde ai tuoi interessi.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-interessi-lista',
      content: 'Per ogni interesse puoi attivare o disattivare le notifiche, oppure eliminarlo.',
      placement: 'top' as const
    }
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  // Funzione per formattare gli importi
  const getImportiPrincipali = (tariffe?: Tariffe) => {
    if (!tariffe) return null;

    const importi: string[] = [];

    const formatImporto = (valore: any) => {
      const num = typeof valore === 'string' ? parseFloat(valore) : valore;
      return !isNaN(num) && num > 0 ? num.toFixed(2) : null;
    };

    const labels: Record<string, string> = {
      diritti: 'Diritti', bolli1: 'Bolli', bolli2: 'Bolli 2', costo: 'Costo Targa',
      gas: 'Gas Compressi', contributi: 'Contributi', diritti2: 'Diritti Stampato',
      diritti3: 'Diritti Esami', diritti4: 'Diritti Idoneità', dirittiSted: 'Diritti STED',
      corrispettivoSted: 'Corrispettivo STED', tributiSpeciali: 'Tributi Speciali',
      rimborsoMissioni: 'Rimborso Missioni',
    };

    for (const [key, label] of Object.entries(labels)) {
      const val = formatImporto((tariffe as any)[key]);
      if (val) importi.push(`${label}: €${val}`);
    }

    return importi.length > 0 ? importi.join(' • ') : null;
  };

  const toggleInteresseExpanded = (interesseId: string) => {
    setExpandedInteressi(prev => {
      const newSet = new Set(prev);
      if (newSet.has(interesseId)) {
        newSet.delete(interesseId);
      } else {
        newSet.add(interesseId);
      }
      return newSet;
    });
  };

  const toggleTariffaExpanded = (index: number) => {
    setExpandedTariffe(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }

    loadInteressi();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (showAddModal && searchQuery.length >= 2) {
        cercaTariffe();
      } else {
        setTariffeDisponibili([]);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [searchQuery, tariffarioSelezionato, showAddModal]);

  const loadInteressi = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/voucher-interessi`,
        { headers: getAuthHeader() }
      );
      setInteressi(response.data.data);
    } catch (error: any) {
      console.error('Errore caricamento interessi:', error);
      toast.error(error.response?.data?.message || 'Errore durante il caricamento');
    } finally {
      setLoading(false);
    }
  };

  const cercaTariffe = async () => {
    try {
      setSearchLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/voucher-interessi/tariffe-disponibili?tariffario=${tariffarioSelezionato}&q=${searchQuery}`,
        { headers: getAuthHeader() }
      );
      setTariffeDisponibili(response.data.data);
    } catch (error: any) {
      console.error('Errore ricerca tariffe:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAggiungiInteresse = async (codiceTariffa: string) => {
    try {
      setAddingInteresse(true);
      await axios.post(
        `${API_BASE_URL}/api/voucher-interessi`,
        {
          tariffario: tariffarioSelezionato,
          codiceTariffa,
          attivo: true,
          notificheEmail: true
        },
        { headers: getAuthHeader() }
      );
      toast.success('Interesse aggiunto con successo!');
      setShowAddModal(false);
      setSearchQuery('');
      setTariffeDisponibili([]);
      loadInteressi();
    } catch (error: any) {
      console.error('Errore aggiunta interesse:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'aggiunta');
    } finally {
      setAddingInteresse(false);
    }
  };

  const handleToggleAttivo = async (interesseId: string, attivo: boolean) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/voucher-interessi/${interesseId}`,
        { attivo: !attivo },
        { headers: getAuthHeader() }
      );
      toast.success(attivo ? 'Interesse disattivato' : 'Interesse attivato');
      loadInteressi();
    } catch (error: any) {
      console.error('Errore toggle attivo:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'aggiornamento');
    }
  };

  const handleToggleNotifiche = async (interesseId: string, notificheEmail: boolean) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/voucher-interessi/${interesseId}`,
        { notificheEmail: !notificheEmail },
        { headers: getAuthHeader() }
      );
      toast.success(notificheEmail ? 'Notifiche disattivate' : 'Notifiche attivate');
      loadInteressi();
    } catch (error: any) {
      console.error('Errore toggle notifiche:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'aggiornamento');
    }
  };

  const handleEliminaInteresse = async (interesseId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo interesse?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/voucher-interessi/${interesseId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Interesse eliminato');
      loadInteressi();
    } catch (error: any) {
      console.error('Errore eliminazione interesse:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'eliminazione');
    }
  };

  const getTariffarioLabel = (tariffario: string) => {
    const map: { [key: string]: string } = {
      'NAZIONALE': 'Nazionale',
      'TRENTO': 'Trento',
      'BOLZANO': 'Bolzano',
      'SICILIA': 'Sicilia',
      'AOSTA': 'Valle d\'Aosta',
      'FRIULI_VENEZIA_GIULIA': 'Friuli V.G.'
    };
    return map[tariffario] || tariffario;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/area-riservata/rimborso-voucher')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Torna ai Voucher
          </button>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Star className="w-8 h-8 text-yellow-400" />
                I miei interessi
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Specifica le tariffe di tuo interesse e ricevi notifiche automatiche
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={startTour}
                className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
                title="Guida"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <button
                id="tour-interessi-aggiungi"
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                <Plus className="w-5 h-5" />
                Aggiungi interesse
              </button>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div id="tour-interessi-info" className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <h3 className="text-2xl font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                Come funziona il sistema interessi
              </h3>
              <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
                <li>✅ Specifica i codici tariffa che ti interessano (es. N003, N015)</li>
                <li>✅ Ricevi email automatiche quando un associato carica un voucher corrispondente</li>
                <li>✅ Visualizza in evidenza i voucher di tuo interesse nella dashboard</li>
                <li>✅ Attiva/disattiva notifiche per ogni interesse in qualsiasi momento</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Lista Interessi */}
        <div id="tour-interessi-lista" className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Interessi configurati ({interessi.length})
            </h2>
          </div>

          <div className="p-6">
            {interessi.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Non hai ancora configurato nessun interesse
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  Aggiungi il primo interesse
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {interessi.map((interesse) => (
                  <div
                    key={interesse._id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded">
                            {interesse.codiceTariffa}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {getTariffarioLabel(interesse.tariffario)}
                          </span>
                          {interesse.attivo ? (
                            <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 px-2 py-1 rounded">
                              Attivo
                            </span>
                          ) : (
                            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 px-2 py-1 rounded">
                              Disattivato
                            </span>
                          )}
                        </div>

                        {interesse.pratiche && interesse.pratiche.length > 0 && (
                          <div className="mb-3">
                            <button
                              onClick={() => toggleInteresseExpanded(interesse._id)}
                              className="flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 mb-2"
                            >
                              {expandedInteressi.has(interesse._id) ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  Nascondi {interesse.pratiche.length} {interesse.pratiche.length === 1 ? 'pratica' : 'pratiche'}
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  Mostra {interesse.pratiche.length} {interesse.pratiche.length === 1 ? 'pratica' : 'pratiche'}
                                </>
                              )}
                            </button>

                            {expandedInteressi.has(interesse._id) && (
                              <div className="space-y-2 pl-4 border-l-2 border-purple-200 dark:border-purple-800">
                                {interesse.pratiche.map((pratica, idx) => (
                                  <div key={idx} className="text-sm">
                                    <p className="text-gray-900 dark:text-gray-100 font-medium">
                                      {pratica.descrizionePratica}
                                    </p>
                                    <p className="text-gray-500 dark:text-gray-400 text-xs">
                                      {pratica.categoria} • {pratica.catalogo}
                                    </p>
                                    {getImportiPrincipali(pratica.tariffe) && (
                                      <p className="text-green-600 dark:text-green-400 text-xs font-medium mt-1">
                                        💰 {getImportiPrincipali(pratica.tariffe)}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleToggleAttivo(interesse._id, interesse.attivo)}
                            className={`text-sm font-medium ${
                              interesse.attivo
                                ? 'text-orange-600 dark:text-orange-400 hover:text-orange-700'
                                : 'text-green-600 dark:text-green-400 hover:text-green-700'
                            }`}
                          >
                            {interesse.attivo ? 'Disattiva' : 'Attiva'}
                          </button>

                          <button
                            onClick={() => handleToggleNotifiche(interesse._id, interesse.notificheEmail)}
                            className="flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700"
                          >
                            {interesse.notificheEmail ? (
                              <>
                                <Bell className="w-4 h-4" />
                                Notifiche ON
                              </>
                            ) : (
                              <>
                                <BellOff className="w-4 h-4" />
                                Notifiche OFF
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      <button
                        onClick={() => handleEliminaInteresse(interesse._id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 p-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Aggiungi Interesse */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Aggiungi Nuovo Interesse
              </h3>

              {/* Tariffario */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tariffario
                </label>
                <select
                  value={tariffarioSelezionato}
                  onChange={(e) => {
                    setTariffarioSelezionato(e.target.value);
                    setSearchQuery('');
                    setTariffeDisponibili([]);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="NAZIONALE">Nazionale</option>
                  <option value="TRENTO">Provincia Autonoma di Trento</option>
                  <option value="BOLZANO">Provincia Autonoma di Bolzano</option>
                  <option value="SICILIA">Regione Sicilia</option>
                  <option value="AOSTA">Valle d'Aosta</option>
                  <option value="FRIULI_VENEZIA_GIULIA">Friuli Venezia Giulia</option>
                </select>
              </div>

              {/* Ricerca Tariffa */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cerca Codice Tariffa
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Cerca per codice tariffa o descrizione pratica..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                  {searchLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Risultati Ricerca */}
              {tariffeDisponibili.length > 0 && (
                <div className="mb-4 max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  {tariffeDisponibili.map((tariffa, index) => (
                    <div
                      key={index}
                      className="p-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded">
                              {tariffa.codiceTariffa}
                            </span>
                            <button
                              onClick={() => toggleTariffaExpanded(index)}
                              className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-900/30 flex items-center gap-1"
                            >
                              {expandedTariffe.has(index) ? (
                                <>
                                  <ChevronUp className="w-3 h-3" />
                                  Nascondi
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-3 h-3" />
                                  {tariffa.pratiche.length} {tariffa.pratiche.length === 1 ? 'pratica' : 'pratiche'}
                                </>
                              )}
                            </button>
                          </div>

                          {expandedTariffe.has(index) && (
                            <div className="space-y-2 pl-4 border-l-2 border-purple-200 dark:border-purple-800">
                              {tariffa.pratiche.map((pratica, idx) => (
                                <div key={idx} className="text-sm">
                                  <p className="text-gray-900 dark:text-gray-100 font-medium">
                                    {pratica.descrizionePratica}
                                  </p>
                                  <p className="text-gray-500 dark:text-gray-400 text-xs">
                                    {pratica.categoria} • {pratica.catalogo}
                                  </p>
                                  {getImportiPrincipali(pratica.tariffe) && (
                                    <p className="text-green-600 dark:text-green-400 text-xs font-medium mt-1">
                                      💰 {getImportiPrincipali(pratica.tariffe)}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleAggiungiInteresse(tariffa.codiceTariffa)}
                          disabled={addingInteresse}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                        >
                          {addingInteresse ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                          Aggiungi
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {searchQuery.length >= 2 && !searchLoading && tariffeDisponibili.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nessuna tariffa trovata per "{searchQuery}"
                </div>
              )}

              {searchQuery.length < 2 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Digita almeno 2 caratteri per cercare
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSearchQuery('');
                    setTariffeDisponibili([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RimborsoVoucherInteressiPage;

